import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const empty = { name: "", username: "", email: "", password: "", phone: "", role: "executive" };
const inputCls = "bg-[#060B14] border border-[#1E293B] focus:border-[#D4AF37] outline-none rounded-md px-3 py-2 text-white text-sm";

function PasswordCell({ id, known, onReset }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="inline-flex items-center gap-3 border border-[#D4AF37]/60 rounded-lg px-3 py-2 min-w-[200px] justify-between" data-testid={`user-password-cell-${id}`}>
      <span className="text-white tracking-[0.2em]" data-testid={`user-password-value-${id}`}>{open && known ? known : "••••••••"}</span>
      {known ? (
        <button type="button" onClick={() => setOpen(o => !o)} data-testid={`user-password-eye-${id}`} aria-label="Show new password" className="text-[#D4AF37] hover:text-[#F2CE6E]">
          {open ? <EyeSlash size={16} /> : <Eye size={16} />}
        </button>
      ) : (
        <button type="button" onClick={onReset} data-testid={`user-password-reset-${id}`} className="text-xs text-[#D4AF37] hover:underline">Reset</button>
      )}
    </div>
  );
}

function ResetPasswordDialog({ user, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post(`/users/${user.id}/reset-password`, { password: pw });
      toast.success(`Password updated for ${user.name}`);
      onSaved(user.id, pw);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to reset password");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!user} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0A1120] border-[#1E293B] text-white max-w-sm" data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle className="font-serif">Reset Password — {user?.name}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="relative">
            <input autoFocus type={show ? "text" : "password"} autoComplete="new-password" placeholder="New password (min 6)" value={pw} onChange={e => setPw(e.target.value)} className={`${inputCls} w-full pr-9`} data-testid="reset-password-input" />
            <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => setShow(s => !s)} data-testid="reset-password-toggle" aria-label={show ? "Hide password" : "Show password"} className="absolute inset-y-0 right-0 z-10 flex items-center px-2.5 text-slate-400 hover:text-[#D4AF37]">
              {show ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-300" data-testid="reset-password-cancel">Cancel</button>
            <button disabled={saving} className="px-4 py-2 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold text-sm hover:bg-[#F2CE6E] disabled:opacity-50" data-testid="reset-password-save">{saving ? "Saving…" : "Save Password"}</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(empty);
  const [showPw, setShowPw] = useState(false);
  const [newPw, setNewPw] = useState({});
  const [resetUser, setResetUser] = useState(null);
  const pwRef = useRef(null);
  const togglePw = () => {
    setShowPw(s => !s);
    requestAnimationFrame(() => {
      const el = pwRef.current;
      if (!el) return;
      el.focus();
      const end = el.value.length;
      el.setSelectionRange(end, end);
    });
  };
  const errMsg = (err) => {
    const d = err?.response?.data?.detail;
    return Array.isArray(d) ? d.map(x => x.msg).join(" ") : (d || "Failed");
  };

  const setUsername = async (u) => {
    const v = window.prompt(`Set username for ${u.name}:`, u.username || "");
    if (v === null) return;
    try { await api.patch(`/users/${u.id}/username`, { username: v }); toast.success("Username saved"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const resetPassword = (u) => setResetUser(u);
  const onPwSaved = (id, pw) => { setNewPw(m => ({ ...m, [id]: pw })); setResetUser(null); };

  const load = () => api.get("/users").then(r => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Account created");
      setForm(empty);
      setShowPw(false);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete account for ${u.name} (${u.email})?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Account deleted");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail?.toString() || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37]">Team</div>
        <h1 className="font-serif text-3xl md:text-4xl text-white mt-1">User Accounts</h1>
      </div>
      <form onSubmit={submit} className="card-luxury p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3" data-testid="user-create-form">
        <input required placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="user-name" />
        <input required placeholder="Username" autoComplete="off" value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} className={inputCls} data-testid="user-username" />
        <input required type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="user-email" />
        <div className="relative">
          <input ref={pwRef} required minLength={6} type={showPw ? "text" : "password"} autoComplete="new-password" placeholder="Password" value={form.password} onChange={e => { const v = e.target.value; setForm(f => ({ ...f, password: v })); }} className={`${inputCls} w-full pr-9`} data-testid="user-password" />
          <button type="button" onMouseDown={e => e.preventDefault()} onClick={togglePw} data-testid="user-password-toggle" aria-label={showPw ? "Hide password" : "Show password"} aria-pressed={showPw} className="absolute inset-y-0 right-0 z-10 flex items-center px-2.5 text-slate-400 hover:text-[#D4AF37] cursor-pointer">
            {showPw ? <EyeSlash size={16} /> : <Eye size={16} />}
          </button>
        </div>
        <input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="user-phone" />
        <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className={inputCls} data-testid="user-role">
          <option value="executive">Sales Executive</option>
          <option value="admin">Admin</option>
        </select>
        <button data-testid="user-create-btn" className="px-4 py-2 rounded-md bg-[#D4AF37] text-[#060B14] font-semibold text-sm hover:bg-[#F2CE6E]">Create Account</button>
      </form>
      <div className="card-luxury overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#060B14] border-b border-[#1E293B]">
            <tr className="text-left text-[10px] uppercase tracking-widest text-slate-400">
              <th className="px-4 py-3">Name</th><th className="px-4 py-3">Username</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Password</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b border-[#1E293B] align-top" data-testid="user-row">
                <td className="px-4 py-3 text-white">{u.name}</td>
                <td className="px-4 py-3" data-testid={`user-username-${u.id}`}>{u.username ? <span className="text-slate-200">{u.username}</span> : <span className="text-amber-400 text-xs">Not set (logs in with email)</span>}</td>
                <td className="px-4 py-3 text-slate-300">{u.email}</td>
                <td className="px-4 py-3 text-slate-300">{u.phone || "—"}</td>
                <td className="px-4 py-3"><PasswordCell id={u.id} known={newPw[u.id]} onReset={() => resetPassword(u)} /></td>
                <td className="px-4 py-3 text-[#D4AF37] text-sm">{u.role === "admin" ? "Admin" : "Executive"}</td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <div className="flex flex-col items-start gap-1.5 text-sm">
                    <button onClick={() => setUsername(u)} data-testid={`user-set-username-${u.id}`} className="text-[#D4AF37] hover:underline">Edit Username</button>
                    <button onClick={() => remove(u)} data-testid={`user-delete-${u.id}`} className="text-rose-400 hover:underline">Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {resetUser && <ResetPasswordDialog user={resetUser} onClose={() => setResetUser(null)} onSaved={onPwSaved} />}
    </div>
  );
}
